import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Newspaper } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Homepage headline ticker.
 * - Pulls the latest published articles from /api/news
 * - Renders as a horizontal marquee (CSS animation, pauses on hover)
 * - Duplicates the headline list once so the loop is seamless
 * - Renders nothing when there are no articles (keeps the homepage clean)
 */
const NewsTicker = () => {
  const [items, setItems] = useState([]);
  const [tickerSpeed, setTickerSpeed] = useState("normal");

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [nRes, sRes] = await Promise.all([
          fetch(`${BACKEND}/api/news`),
          fetch(`${BACKEND}/api/settings`),
        ]);
        if (nRes.ok && alive) {
          const list = await nRes.json();
          setItems(Array.isArray(list) ? list.slice(0, 10) : []);
        }
        if (sRes.ok && alive) {
          const s = await sRes.json();
          if (s && s.ticker_speed) setTickerSpeed(s.ticker_speed);
        }
      } catch {
        /* ignore */
      }
    };
    load();
    // Refresh every 3 min so a freshly published story shows up without a reload
    const id = setInterval(load, 180000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  if (items.length === 0) return null;

  // Duplicate for a seamless CSS marquee
  const loop = [...items, ...items];
  // Base "normal" is ~6 seconds per item. Slow doubles, fast halves.
  const perItem = tickerSpeed === "slow" ? 12 : tickerSpeed === "fast" ? 3 : 6;
  const durationSec = Math.max(15, items.length * perItem);

  return (
    <div
      data-testid="news-ticker"
      className="relative border-y border-gold/25 bg-black/70 backdrop-blur-md overflow-hidden"
    >
      <div className="max-w-7xl mx-auto flex items-stretch">
        {/* Red "LATEST NEWS" badge */}
        <div className="shrink-0 flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-crimson to-crimson-live text-white font-condensed uppercase tracking-[0.28em] text-[11px] border-r border-black/40">
          <span className="relative flex w-2 h-2">
            <span className="absolute inset-0 rounded-full bg-white/80 live-ping" />
            <span className="relative inline-flex w-2 h-2 rounded-full bg-white live-dot" />
          </span>
          Latest News
        </div>

        {/* Scrolling headlines */}
        <div className="relative flex-1 overflow-hidden">
          <div
            className="ticker-track flex items-center gap-10 whitespace-nowrap py-2.5"
            style={{ animationDuration: `${durationSec}s` }}
          >
            {loop.map((a, i) => (
              <Link
                key={`${a.id}-${i}`}
                to="/news"
                data-testid={i < items.length ? `ticker-item-${a.id}` : undefined}
                className="inline-flex items-center gap-3 group"
              >
                {a.category && (
                  <span className="font-condensed uppercase tracking-widest text-[10px] text-gold/80">
                    {a.category}
                  </span>
                )}
                <span className="font-condensed uppercase tracking-wide text-[13px] text-neutral-100 group-hover:text-gold transition-colors">
                  {a.title}
                </span>
                <span className="text-gold/60" aria-hidden>
                  •
                </span>
              </Link>
            ))}
          </div>
          {/* Edge fades so text disappears smoothly */}
          <div className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-black/80 to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-black/80 to-transparent" />
        </div>

        {/* Read All CTA (hidden on very small screens to save room for the marquee) */}
        <Link
          to="/news"
          data-testid="ticker-read-all"
          className="hidden sm:inline-flex shrink-0 items-center gap-2 px-4 border-l border-gold/25 text-gold hover:bg-gold/10 font-condensed uppercase tracking-widest text-[10px] transition-colors"
        >
          <Newspaper className="w-3.5 h-3.5" />
          Read All
        </Link>
      </div>
    </div>
  );
};

export default NewsTicker;
